import { useState, type ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import MobileFrame from '@/components/MobileFrame';
import { Icon } from '@/components/Icons';
import { tpl, useT } from '@/lib/i18n';
import { useOnboardingStore } from '@/lib/store';
import { submitDataForm } from '@/lib/onboarding-api';
import { compressAndUpload } from '@/lib/upload';

const requiredKeys: Array<keyof ReturnType<typeof pickRequired>> = [
    'nik',
    'fullName',
    'birthPlace',
    'birthDate',
    'gender',
    'email',
    'password',
    'passwordConfirm',
    'address',
    'city',
    'province',
];

function pickRequired(form: ReturnType<typeof useFormState>) {
    return {
        nik: form.nik,
        fullName: form.fullName,
        birthPlace: form.birthPlace,
        birthDate: form.birthDate,
        gender: form.gender,
        email: form.email,
        password: form.password,
        passwordConfirm: form.passwordConfirm,
        address: form.address,
        city: form.city,
        province: form.province,
    };
}

function useFormState() {
    return useOnboardingStore((s) => s.formData);
}

export default function DataFormScreen() {
    const t = useT();
    const navigate = useNavigate();
    const formData = useOnboardingStore((s) => s.formData);
    const updateFormData = useOnboardingStore((s) => s.updateFormData);
    const setRegistrationId = useOnboardingStore((s) => s.setRegistrationId);
    const setCertStatus = useOnboardingStore((s) => s.setCertStatus);
    const setVerification = useOnboardingStore((s) => s.setVerification);

    const [submitting, setSubmitting] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    // Upload state per file — supaya UX kasih feedback progress
    type UploadState = { status: 'idle' | 'uploading' | 'done' | 'error'; error?: string };
    const [ktpUpload, setKtpUpload] = useState<UploadState>({ status: 'idle' });
    const [npwpUpload, setNpwpUpload] = useState<UploadState>({ status: 'idle' });

    const picked = pickRequired(formData);
    const allFilled = requiredKeys.every((k) => {
        const val = picked[k];
        return typeof val === 'string' && val.length > 0;
    });
    const ktpReady = !!formData.ktpPhotoUrl && ktpUpload.status !== 'uploading';
    const npwpInProgress = npwpUpload.status === 'uploading';
    const passwordOk =
        formData.password.length >= 8 && formData.password === formData.passwordConfirm;
    const canSubmit = allFilled && ktpReady && passwordOk && !npwpInProgress && !submitting;

    /** Handler: kompres + upload direct ke MinIO, simpan URL di store. */
    const uploadFile =
        (kind: 'ktp' | 'npwp') => async (e: ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0];
            if (!file) return;

            const setState = kind === 'ktp' ? setKtpUpload : setNpwpUpload;
            const urlKey = kind === 'ktp' ? 'ktpPhotoUrl' : 'npwpPhotoUrl';
            const previewKey = kind === 'ktp' ? 'ktpPhotoPreview' : 'npwpPhotoPreview';

            setState({ status: 'uploading' });

            // Set preview langsung (data URL) — supaya user lihat foto yang dipilih
            const reader = new FileReader();
            reader.onload = () => {
                updateFormData(previewKey, reader.result as string);
            };
            reader.readAsDataURL(file);

            try {
                const { url, originalSize, finalSize } = await compressAndUpload(file, kind, {
                    maxSize: 1920,
                    quality: 0.85,
                });
                updateFormData(urlKey, url);
                setState({ status: 'done' });
                console.info(
                    `[${kind}] compressed ${(originalSize / 1024).toFixed(0)}KB → ${(finalSize / 1024).toFixed(0)}KB`
                );
            } catch (err) {
                const message = err instanceof Error ? err.message : 'Upload gagal';
                setState({ status: 'error', error: message });
                updateFormData(urlKey, null);
                updateFormData(previewKey, null);
            }
        };

    const handleSubmit = async () => {
        if (!canSubmit || !formData.ktpPhotoUrl) return;
        try {
            setSubmitting(true);
            setErr(null);

            const result = await submitDataForm({
                nik: formData.nik,
                fullName: formData.fullName,
                email: formData.email,
                password: formData.password,
                password_confirmation: formData.passwordConfirm,
                dateOfBirth: formData.birthDate,
                placeOfBirth: formData.birthPlace,
                gender: formData.gender as 'M' | 'F',
                address: formData.address,
                city: formData.city,
                province: formData.province,
                ktpPhotoUrl: formData.ktpPhotoUrl,
                npwpPhotoUrl: formData.npwpPhotoUrl ?? undefined,
                npwp: formData.npwp || undefined,
            });

            setRegistrationId(result.registrationId);
            setCertStatus(result.status);
            setVerification({ registration: 'ok' });
            navigate('/onboarding/liveness');
        } catch (e) {
            setErr(e instanceof Error ? e.message : t.somethingWrong);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <MobileFrame
            progress={(4 / 7) * 100}
            showBack
            stepLabel={tpl(t.stepOf, { current: 4, total: 7 })}
        >
            <div
                className="fade-in"
                style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '8px 24px 28px',
                }}
            >
                <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 8 }}>{t.dataTitle}</h1>
                <p
                    style={{
                        fontSize: 14,
                        lineHeight: 1.55,
                        color: 'var(--ink-500)',
                        marginBottom: 20,
                    }}
                >
                    {t.dataSubtitle}
                </p>

                <div
                    style={{
                        display: 'flex',
                        gap: 10,
                        padding: '10px 12px',
                        marginBottom: 18,
                        background: 'var(--success-soft)',
                        borderRadius: 10,
                        border: '1px solid rgba(21,128,61,0.15)',
                    }}
                >
                    <Icon name="shieldCheck" size={18} color="var(--success)" />
                    <span style={{ fontSize: 12, lineHeight: 1.4, color: 'var(--success)' }}>
                        Data akan diverifikasi otomatis melalui <b>Dukcapil Kemendagri</b>
                    </span>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <Field label={t.labelNik}>
                        <input
                            className="field-input"
                            value={formData.nik}
                            onChange={(e) =>
                                updateFormData('nik', e.target.value.replace(/\D/g, '').slice(0, 16))
                            }
                            placeholder="3201•••••••••••"
                            inputMode="numeric"
                            maxLength={16}
                        />
                    </Field>
                    <Field label={t.labelName}>
                        <input
                            className="field-input"
                            value={formData.fullName}
                            onChange={(e) => updateFormData('fullName', e.target.value.toUpperCase())}
                            placeholder="SEPERTI DI KTP"
                        />
                    </Field>
                    <Field label={t.labelBirthPlace}>
                        <input
                            className="field-input"
                            value={formData.birthPlace}
                            onChange={(e) => updateFormData('birthPlace', e.target.value)}
                            placeholder="Jakarta"
                        />
                    </Field>
                    <Field label={t.labelBirthDate}>
                        <input
                            className="field-input"
                            type="date"
                            value={formData.birthDate}
                            onChange={(e) => updateFormData('birthDate', e.target.value)}
                        />
                    </Field>
                    <Field label={t.labelGender}>
                        <select
                            className="field-input"
                            value={formData.gender}
                            onChange={(e) =>
                                updateFormData('gender', e.target.value as 'M' | 'F' | '')
                            }
                        >
                            <option value="" disabled>
                                Pilih…
                            </option>
                            <option value="M">{t.genderMale}</option>
                            <option value="F">{t.genderFemale}</option>
                        </select>
                    </Field>
                    <Field label={t.labelEmail}>
                        <input
                            className="field-input"
                            type="email"
                            value={formData.email}
                            onChange={(e) => updateFormData('email', e.target.value)}
                            placeholder="nama@email.com"
                        />
                    </Field>
                    <Field
                        label={t.labelPassword}
                        hint={
                            formData.password.length > 0 && formData.password.length < 8
                                ? `Password minimal 8 karakter (${formData.password.length}/8)`
                                : t.passwordHint
                        }
                        hintVariant={
                            formData.password.length > 0 && formData.password.length < 8
                                ? 'error'
                                : 'default'
                        }
                    >
                        <input
                            className={`field-input${formData.password.length > 0 && formData.password.length < 8 ? ' error' : ''}`}
                            type="password"
                            value={formData.password}
                            onChange={(e) => updateFormData('password', e.target.value)}
                            placeholder="••••••••"
                        />
                    </Field>
                    <Field
                        label={t.labelPasswordConfirm}
                        hint={
                            formData.passwordConfirm.length > 0 &&
                            formData.password !== formData.passwordConfirm
                                ? 'Password tidak sama dengan konfirmasi'
                                : formData.passwordConfirm.length > 0 &&
                                    formData.password === formData.passwordConfirm &&
                                    formData.password.length >= 8
                                  ? '✓ Password cocok'
                                  : undefined
                        }
                        hintVariant={
                            formData.passwordConfirm.length > 0 &&
                            formData.password !== formData.passwordConfirm
                                ? 'error'
                                : formData.passwordConfirm.length > 0 &&
                                    formData.password === formData.passwordConfirm &&
                                    formData.password.length >= 8
                                  ? 'success'
                                  : 'default'
                        }
                    >
                        <input
                            className={`field-input${formData.passwordConfirm.length > 0 && formData.password !== formData.passwordConfirm ? ' error' : ''}`}
                            type="password"
                            value={formData.passwordConfirm}
                            onChange={(e) => updateFormData('passwordConfirm', e.target.value)}
                            placeholder="••••••••"
                        />
                    </Field>
                    <Field label={t.labelAddress}>
                        <textarea
                            className="field-input"
                            style={{ height: 70, padding: 12, resize: 'none', lineHeight: 1.4 }}
                            value={formData.address}
                            onChange={(e) => updateFormData('address', e.target.value)}
                            placeholder="Jl. ..."
                        />
                    </Field>
                    <Field label={t.labelCity}>
                        <input
                            className="field-input"
                            value={formData.city}
                            onChange={(e) => updateFormData('city', e.target.value)}
                        />
                    </Field>
                    <Field label={t.labelProvince}>
                        <input
                            className="field-input"
                            value={formData.province}
                            onChange={(e) => updateFormData('province', e.target.value)}
                        />
                    </Field>

                    <Field label={t.labelKtpPhoto} hint={t.ktpPhotoHint}>
                        <FilePicker
                            preview={formData.ktpPhotoPreview}
                            uploadStatus={ktpUpload.status}
                            uploadError={ktpUpload.error}
                            onChange={uploadFile('ktp')}
                            accept="image/*"
                            capture="environment"
                        />
                    </Field>
                    <Field label={t.labelNpwp}>
                        <input
                            className="field-input"
                            value={formData.npwp}
                            onChange={(e) => updateFormData('npwp', e.target.value)}
                            placeholder="00.000.000.0-000.000"
                        />
                    </Field>
                    <Field label={t.labelNpwpPhoto}>
                        <FilePicker
                            preview={formData.npwpPhotoPreview}
                            uploadStatus={npwpUpload.status}
                            uploadError={npwpUpload.error}
                            onChange={uploadFile('npwp')}
                            accept="image/*"
                        />
                    </Field>
                </div>

                {err && (
                    <div
                        className="chip chip-error"
                        style={{ marginTop: 16, alignSelf: 'flex-start' }}
                    >
                        <Icon name="alert" size={14} /> {err}
                    </div>
                )}

                <div style={{ height: 20 }} />

                <button
                    type="button"
                    className="btn btn-primary"
                    style={{ width: '100%', marginTop: 8 }}
                    disabled={!canSubmit}
                    onClick={handleSubmit}
                >
                    {submitting ? <div className="spinner" /> : t.continue}
                    {!submitting && <Icon name="arrowRight" size={18} />}
                </button>
            </div>
        </MobileFrame>
    );
}

function Field({
    label,
    hint,
    hintVariant = 'default',
    children,
}: {
    label: string;
    hint?: string;
    hintVariant?: 'default' | 'error' | 'success';
    children: React.ReactNode;
}) {
    const hintStyle =
        hintVariant === 'success' ? { color: 'var(--success)' } : undefined;
    const hintClass = `field-hint${hintVariant === 'error' ? ' error' : ''}`;
    return (
        <div>
            <label className="field-label">{label}</label>
            {children}
            {hint && (
                <p className={hintClass} style={hintStyle}>
                    {hint}
                </p>
            )}
        </div>
    );
}

function FilePicker({
    preview,
    uploadStatus,
    uploadError,
    onChange,
    accept,
    capture,
}: {
    preview: string | null;
    uploadStatus: 'idle' | 'uploading' | 'done' | 'error';
    uploadError?: string;
    onChange: (e: ChangeEvent<HTMLInputElement>) => void;
    accept: string;
    capture?: 'environment' | 'user';
}) {
    const { icon, color, label } = (() => {
        switch (uploadStatus) {
            case 'uploading':
                return { icon: 'refresh' as const, color: 'var(--warning)', label: 'Mengupload…' };
            case 'done':
                return { icon: 'checkCircle' as const, color: 'var(--success)', label: 'Terupload — klik untuk ganti' };
            case 'error':
                return { icon: 'alert' as const, color: 'var(--error)', label: uploadError ?? 'Upload gagal — klik coba lagi' };
            default:
                return { icon: 'camera' as const, color: 'var(--ink-500)', label: 'Ambil foto / pilih file…' };
        }
    })();

    return (
        <div>
            {preview && (
                <img
                    src={preview}
                    alt="preview"
                    style={{
                        width: '100%',
                        maxHeight: 160,
                        objectFit: 'contain',
                        borderRadius: 10,
                        background: 'var(--ink-50)',
                        marginBottom: 8,
                        border: '1px solid var(--ink-200)',
                    }}
                />
            )}
            <label
                className={`field-input${uploadStatus === 'error' ? ' error' : ''}`}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    cursor: uploadStatus === 'uploading' ? 'wait' : 'pointer',
                    minHeight: 48,
                    padding: '0 14px',
                }}
            >
                <Icon
                    name={icon}
                    size={18}
                    color={color}
                    {...(uploadStatus === 'uploading'
                        ? { style: { animation: 'spin 0.8s linear infinite' } as React.CSSProperties } as object
                        : {})}
                />
                <span
                    style={{
                        fontSize: 14,
                        color: uploadStatus === 'done' ? 'var(--ink-900)' : color,
                        flex: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {label}
                </span>
                <input
                    type="file"
                    accept={accept}
                    capture={capture}
                    onChange={onChange}
                    disabled={uploadStatus === 'uploading'}
                    style={{ display: 'none' }}
                />
            </label>
        </div>
    );
}
